import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupee } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Save, Download, AlertTriangle } from "lucide-react";

export async function downloadExport(entity) {
  const res = await api.get(`/admin/export/${entity}`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `${entity}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminInventory() {
  const [items, setItems] = useState(null);
  const [lowOnly, setLowOnly] = useState(false);
  const [edits, setEdits] = useState({});

  const load = () => api.get("/admin/products?limit=200").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const saveStock = async (p) => {
    const v = edits[p.id];
    if (v === undefined || v === "") return;
    await api.put(`/admin/products/${p.id}/stock`, { stock: parseInt(v, 10) });
    toast.success(`Stock updated: ${p.name}`);
    setEdits((e) => { const n = { ...e }; delete n[p.id]; return n; });
    load();
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  const rows = lowOnly ? items.filter((p) => (p.stock ?? 0) <= 10) : items;
  const lowCount = items.filter((p) => (p.stock ?? 0) <= 10).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Inventory</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLowOnly((v) => !v)} data-testid="inventory-low-toggle" className="rounded-full border-forest text-forest">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> {lowOnly ? "Show all" : `Low stock (${lowCount})`}
          </Button>
          <Button onClick={() => downloadExport("products")} data-testid="export-products-btn" className="rounded-full bg-forest hover:bg-earth text-cream"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sand text-olive">
            <tr>
              <th className="text-left px-4 py-3 font-500">Product</th>
              <th className="text-left px-4 py-3 font-500 hidden sm:table-cell">SKU</th>
              <th className="text-left px-4 py-3 font-500">Price</th>
              <th className="text-left px-4 py-3 font-500">Stock</th>
              <th className="text-left px-4 py-3 font-500">Update</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const stock = p.stock ?? 0;
              return (
                <tr key={p.id} className="border-t border-sand" data-testid={`inventory-row-${p.id}`}>
                  <td className="px-4 py-3 text-ink max-w-[220px] truncate">{p.name}</td>
                  <td className="px-4 py-3 text-brown/70 hidden sm:table-cell">{p.sku}</td>
                  <td className="px-4 py-3 text-brown/70">{rupee(p.price)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-500 ${stock === 0 ? "bg-terracotta/15 text-terracotta" : stock <= 10 ? "bg-mustard/25 text-earth" : "bg-olive/15 text-olive"}`}>
                      {stock === 0 ? "Out of stock" : `${stock} in stock`}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 items-center">
                      <Input type="number" value={edits[p.id] ?? ""} placeholder={String(stock)} onChange={(e) => setEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                        className="w-20 h-9 rounded-lg bg-sand" data-testid={`inventory-input-${p.id}`} />
                      <Button size="sm" onClick={() => saveStock(p)} data-testid={`inventory-save-${p.id}`} className="rounded-lg bg-forest hover:bg-earth text-cream h-9"><Save className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
